import { Message, Conversation } from '../types/chat';
import { useConnectionStore } from '../store/connectionStore';
import { supabase } from '../lib/supabase';
import { toggleMessageLike, subscribeToMessageLikes, editMessage, deleteMessage } from '../lib/supabase/chat';
import { useAuth } from '@/contexts/AuthContext';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useProfile } from '@/hooks/useProfile';
import { useNotificationStore } from '@/store/notificationStore';  

/**
 * Custom hook to fetch and cache the current user's database ID.
 */
function useUserDbId(user: { id: string } | null) {
  const [userDbId, setUserDbId] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id) return;

    (async () => {
      const { data, error } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user.id)
        .single();
      if (error) {
        console.error('Error fetching user DB id:', error);
      } else if (data) {
        setUserDbId(data.id);
      }
    })();
  }, [user?.id]);

  return userDbId;
}

// Helper to transform message data
const transformMessage = (msg: any, userDbId: string | null, senderAvatar?: string): Message => ({
  id: msg.id,
  content: msg.content,
  time: new Date(msg.created_at),
  isSender: userDbId === msg.sender_id,
  status: msg.read_at ? 'read' : msg.sender_id === userDbId ? 'delivered' : 'sent',
  senderAvatar,
  image: msg.image_url ?? undefined
  
});

function useChat(conversationId?: string) {
  const { user } = useAuth();
  const { profile } = useProfile();
  const { connections, fetchConnections } = useConnectionStore();
const { notifications, markAsRead } = useNotificationStore();

/** Bulk helper: marks each id read in the local store */
const markAsReadBulk = useCallback((ids: string[]): void => {
  ids.forEach(id => markAsRead(id));
}, [markAsRead]);

  
  // Infinite scroll paging state
  const PAGE_SIZE = 1000;
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  const [messages, setMessages] = useState<Message[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [connectedConversations, setConnectedConversations] = useState<Conversation[]>([]);
  const [nonConnectedConversations, setNonConnectedConversations] = useState<Conversation[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [isMessagesLoading, setIsMessagesLoading] = useState(true);
  const [isConversationsLoading, setIsConversationsLoading] = useState(true);
  const isLoading = isConversationsLoading || isMessagesLoading;
  const [error, setError] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [likedMessages, setLikedMessages] = useState<Record<string, boolean>>({});
  const messageChannelRef = useRef<ReturnType<typeof supabase.channel>>();
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const conversationChannelRef = useRef<ReturnType<typeof supabase.channel>>();
  const likesChannelRef = useRef<ReturnType<typeof supabase.channel>>();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // Fetch connections if they are not already loaded
  useEffect(() => {
    if (!connections || connections.length === 0) {
      console.warn("⚠️ No connections found, fetching now...");
      fetchConnections();
    }
  }, [connections, fetchConnections]);

  // Cache the current user's DB ID
  const userDbId = useUserDbId(user);

  // Debug logging (only during development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Chat state:', { conversations, isInitialized, messages, error });
    }
  }, [conversations, isInitialized, messages, error]);

  /**
   * Fetch conversations via an RPC call and transform the result to match our Conversation type.
   */
  const fetchConversations = useCallback(async () => {
    try {
      if (!user?.id) {
        console.warn('No authenticated user');
        return;
      }
      setIsConversationsLoading(true);
      console.log('Fetching conversations...');

      // New RPC function for retrieving conversations
      const { data, error: fetchError } = await supabase.rpc('get_conversations_for_user', {
        user_auth_id: user.id,
      });
      if (fetchError) throw fetchError;

      console.log('Get conversations for user: ', data);
      console.log('Connections: ', connections);

      const transformedConversations = (data || []).map((conv: any) => ({
        id: conv.conversation_id, 
        name: conv.other_user_name,
        image: conv.other_user_avatar || 'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png',
        city: conv.other_user_city,
        lastMessage: conv.last_message,
        time: conv.last_message_at,
        unread: Number(conv.unread_count),
        online: false,
        isConnected: connections.some(conn =>
          conn.other_user?.id === conv.other_user_id &&
          conn.status === 'accepted'
        ),
        otherUserId: conv.other_user_id,
      }));

      console.log('Transformed Conversations', transformedConversations);

      // Separate conversations into connected and non-connected
      setConnectedConversations(transformedConversations.filter(c => c.isConnected));
      setNonConnectedConversations(transformedConversations.filter(c => !c.isConnected));
      setConversations(transformedConversations);
      setIsInitialized(true);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError(err instanceof Error ? err.message : 'Failed to load conversations');
    } finally {
      setIsConversationsLoading(false);
    }
  }, [user?.id, connections]);

  /**
   * Paged fetch messages for a given conversation.
   */
 const fetchMessages = useCallback(async (convId?: string, pageNum = 0) => {
  if (!convId || !userDbId) return;
  console.log('Fetching messages for conversation:', convId);

  try {
    setIsMessagesLoading(true);
    setError(null);

    const start = pageNum * PAGE_SIZE;
    const end = start + PAGE_SIZE - 1;

    // 1) Load the raw page of messages with reply_to_message_id
    const { data: rawMsgs, error: messagesError } = await supabase
      .from('messages')
      .select('id, content, created_at, read_at, sender_id, reply_to_message_id, image_url')
      .eq('conversation_id', convId)
      .order('created_at', { ascending: false }) // newest first
      .range(start, end);
    if (messagesError) throw messagesError;

    // Fetch reply content for messages that have reply_to_message_id
    const replyMessageIds = rawMsgs
      .filter(msg => msg.reply_to_message_id)
      .map(msg => msg.reply_to_message_id);
    
    let replyMessages = {};
    if (replyMessageIds.length > 0) {
      const { data: replyData, error: replyError } = await supabase
        .from('messages')
        .select('id, content, sender_id')
        .in('id', replyMessageIds);
        
      if (!replyError && replyData) {
        replyMessages = replyData.reduce((acc, msg) => {
          acc[msg.id] = msg;
          return acc;
        }, {});
      }
    }

    // --- UPDATED BLOCK: fetch likes count & whether current user liked each message ---
const messageIds = rawMsgs.map(m => m.id);
const { data: likeData, error: likesError } = await supabase
  .from('message_likes')
  .select('message_id, user_id')
  .in('message_id', messageIds);

if (likesError) console.error('Error fetching likes:', likesError);

const likeMap: Record<string, { count: number; likedByMe: boolean }> = {};
(likeData || []).forEach(row => {
  if (!likeMap[row.message_id]) {
    likeMap[row.message_id] = { count: 0, likedByMe: false };
  }
  likeMap[row.message_id].count += 1;
  if (row.user_id === userDbId) likeMap[row.message_id].likedByMe = true;
});

    // ─────────────────────────────────────────────────────────────────────────

   // 2) Reverse + transform + inject isLiked and likesCount
const batch = (rawMsgs || [])
  .reverse()
  .map(msg => {
    const transformedMsg = transformMessage(
      msg,
      userDbId,
      msg.sender_id === userDbId ? profile?.avatar_url : undefined
    );

    if (msg.reply_to_message_id && replyMessages[msg.reply_to_message_id]) {
      transformedMsg.reply_to_message = replyMessages[msg.reply_to_message_id];
    }

    return {
      ...transformedMsg,
      isLiked: likeMap[msg.id]?.likedByMe || false,
      likesCount: likeMap[msg.id]?.count || 0
    };
  });


    // 3) Update hasMore & state
    setHasMore((rawMsgs?.length ?? 0) === PAGE_SIZE);
    if (pageNum === 0) setMessages(batch);
    else setMessages(prev => [...batch, ...prev]);
  } catch (err) {
    console.error('Error fetching messages:', err);
    setError(err instanceof Error ? err.message : 'Failed to load messages');
  } finally {
    setIsMessagesLoading(false);
  }
}, [userDbId, profile]);

  // Initial & paged load when conversationId changes
// ─── ADD THIS INSTEAD ───────────────────────────────
// Initial & paged load when conversationId changes
useEffect(() => {
  setPage(0);
  setHasMore(true);

  if (!conversationId) return;

  // 1) load first page of messages
  fetchMessages(conversationId, 0);

  // 2) mark everything in this convo as read on the server
  supabase
    .rpc('mark_messages_as_read', { conversation_id_param: conversationId })
    .then(({ error }) => {
      if (error) {
        console.error('Failed to mark as read:', error);
      } else {
        // 3) refresh your conversation list so its unread badges reset
        fetchConversations();
      }
    });
}, [conversationId, fetchMessages, fetchConversations]);


  // Infinite-scroll handler
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      if (el.scrollTop < 50 && hasMore && !isMessagesLoading) {
        setPage(p => {
          const next = p + 1;
          fetchMessages(conversationId, next);
          return next;
        });
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [conversationId, fetchMessages, hasMore, isMessagesLoading]);

  // Subscribe to message like status changes
 useEffect(() => {
  if (!conversationId || messages.length === 0) return;

  const visibleMessageIds = messages.map(msg => msg.id);

  const unsubscribe = subscribeToMessageLikes(visibleMessageIds, (messageId, isLiked) => {
    setMessages(prev =>
      prev.map(msg => {
        if (msg.id !== messageId) return msg;

        const countAdjustment = isLiked ? 1 : -1;
        const alreadyLiked = msg.isLiked;

        const adjustedCount =
          alreadyLiked === isLiked
            ? msg.likesCount
            : Math.max(0, (msg.likesCount || 0) + countAdjustment);

        return {
          ...msg,
          isLiked,
          likesCount: adjustedCount
        };
      })
    );

    setLikedMessages(prev => ({
      ...prev,
      [messageId]: isLiked
    }));
  });

  return () => {
    unsubscribe();
  };
}, [conversationId, messages]);

  /**
   * Updated handleSendMessage with AI functionality.
   */
  const AI_AGENT_USER_ID = 'a125a4ca-5ff3-45d6-b8b7-cfe3c015c600';
  const N8N_WEBHOOK_URL = 'https://playlclub.app.n8n.cloud/webhook/e5b7a196-92a3-40b4-98da-6cbb2964dd09/chat';
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 1000;

  const fetchWithRetry = async (url: string, options: RequestInit, retries = MAX_RETRIES): Promise<Response> => {
    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(JSON.stringify({
          status: response.status,
          statusText: response.statusText,
          body: errorBody
        }));
      }
      return response;
    } catch (error) {
      if (retries > 0) {
        console.log(`Retrying webhook call, ${retries} attempts remaining...`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return fetchWithRetry(url, options, retries - 1);
      }
      throw error;
    }
  };

const handleSendMessage = useCallback(
  async (
    convId: string,
    messageContent: string,
    replyToMessage?: Message | null,
    imageUrl?: string
  ) => {
    if (!convId || !messageContent.trim() || !userDbId) return;

    try {
      // 1) Insert the new message row
      const { data, error } = await supabase
        .from('messages')
        .insert({
          conversation_id: convId,
          sender_id:       userDbId,
          content:         messageContent.trim(),
          created_at:      new Date().toISOString(),
          ...(replyToMessage && { reply_to_message_id: replyToMessage.id }),
          ...(imageUrl       && { image_url:        imageUrl }),
        })
        .select()
        .single();
      if (error) throw error;

      // 2) Optimistically update UI
      setMessages(prev =>
        prev.concat(
          transformMessage(
            data,
            userDbId,
            data.sender_id === userDbId ? profile?.avatar_url : undefined
          )
        )
      );
      setReplyingTo(null);

      // ─── Badge‐RPC + AI agent logic ───
      const { data: participants, error: partErr } = await supabase
        .from('conversation_participants')
        .select('user_id')
        .eq('conversation_id', convId);
      if (partErr) {
        console.error('Error fetching participants:', partErr);
        return;
      }

      const recipientId = participants.find(p => p.user_id !== userDbId)?.user_id;
      if (!recipientId) return;

      // ─── Removed the old `if (recipientId !== AI_AGENT_USER_ID) { add_notification_rpc } else { … }` branch ───

      // Only run AI logic if the recipient is the AI agent
      if (recipientId === AI_AGENT_USER_ID) {
        setIsTyping(true);
        try {
          const aiRes = await fetchWithRetry(N8N_WEBHOOK_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Basic ' + btoa(`agent@playclub.ai:!123Wefasd`),
            },
            body: JSON.stringify({
              sender_id: userDbId,
              chatInput: messageContent.trim(),
              sessionId: convId,
              timestamp: new Date().toISOString(),
            }),
          });
          const { output } = await aiRes.json();
          if (!output) throw new Error('Invalid AI response');

          await supabase
            .from('messages')
            .insert({
              conversation_id: convId,
              sender_id:       AI_AGENT_USER_ID,
              content:         output,
              created_at:      new Date().toISOString(),
            });
        } catch (e) {
          console.error('AI webhook error:', e);
        } finally {
          setTimeout(() => setIsTyping(false), 1000);
        }
      }
      // ────────────────────────────────────

    } catch (err) {
      console.error('Error in message handling:', err);
      setError('Unable to send message. Please try again.');
      setIsTyping(false);
    }
  },
  [userDbId, profile?.avatar_url]
);

  /**
   * Handle image upload for a message
   */
  const handleImageUpload = useCallback(async (imageUrl: string) => {
    if (!conversationId || !userDbId) return;
    
    setIsUploadingImage(true);
    try {
      // Send a message with the image
      await handleSendMessage(conversationId, "📷", null, imageUrl);
    } catch (error) {
      console.error('Error sending image message:', error);
      setError('Failed to send image. Please try again.');
    } finally {
      setIsUploadingImage(false);
    }
  }, [conversationId, userDbId, handleSendMessage]);

  /**
   * Handle editing a message
   */
  const handleEditMessage = useCallback(async (messageId: string, newContent: string) => {
    try {
      if (!messageId || !newContent.trim()) return;
      
      // Optimistic update
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, content: newContent } : msg
      ));
      
      // Update in database
      const success = await editMessage(messageId, newContent);
      
      if (!success) {
        // Revert optimistic update if failed
        console.error('Failed to edit message');
      }
      
      // Clear editing state
      setEditingMessageId(null);
    } catch (error) {
      console.error('Error editing message:', error);
    }
  }, []);

  /**
   * Handle deleting a message
   */
  const handleDeleteMessage = useCallback(async (messageId: string) => {
    try {
      if (!messageId) return;
      
      // Optimistic update
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
      
      // Delete from database
      const success = await deleteMessage(messageId);
      
      if (!success) {
        // Revert optimistic update if failed
        console.error('Failed to delete message');
        // Refresh messages to restore the deleted message
        if (conversationId) {
          fetchMessages(conversationId, 0);
        }
      }
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  }, [conversationId, fetchMessages]);

  // Handle liking a message
  const handleLikeMessage = useCallback(async (messageId: string) => {
    try {
      // Optimistic update for immediate UI feedback
      setMessages(prev => prev.map(msg => {
        if (msg.id === messageId) {
          const newIsLiked = !msg.isLiked;
          const newLikesCount = (msg.likesCount || 0) + (newIsLiked ? 1 : -1);
          return {
            ...msg,
            isLiked: newIsLiked,
            likesCount: Math.max(0, newLikesCount)
          };
        }
        return msg;
      }));

      // Update local state of liked messages
      setLikedMessages(prev => {
        const newIsLiked = !prev[messageId];
        return { ...prev, [messageId]: newIsLiked };
      });
      
      // Update in database
      const isLiked = await toggleMessageLike(messageId);
      
      // Sync UI with server state if needed
      setMessages(prev => prev.map(msg => {
        if (msg.id === messageId) {
          const likesCount = (msg.likesCount || 0) + (isLiked ? 1 : -1);
          return {
            ...msg,
            isLiked,
            likesCount: Math.max(0, likesCount)
          };
        }
        return msg;
      }));
    } catch (error) {
      console.error('Error liking message:', error);
      // Revert optimistic update on error by re-fetching messages
      setMessages(prev => [...prev]);
    }
  }, []);

  // Cleanup typing timeout
  useEffect(() => () => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
  }, []);

  /**
   * Subscribe to real-time messages.
   */
  useEffect(() => {
    if (!conversationId) return;
    if (messageChannelRef.current) supabase.removeChannel(messageChannelRef.current);

   messageChannelRef.current = supabase
  .channel(`messages:${conversationId}`)
  .on('postgres_changes', {
    event: 'INSERT',
    schema: 'public',
    table: 'messages',
    filter: `conversation_id=eq.${conversationId}`,
  }, async (payload) => {
    if (payload.new.sender_id !== userDbId) {
      // Fetch original quoted message if it exists
      let originalMessage = null;
      if (payload.new.reply_to_message_id) {
        const { data, error } = await supabase
          .from('messages')
          .select('id, content, sender_id')
          .eq('id', payload.new.reply_to_message_id)
          .single();
        if (!error && data) {
          originalMessage = data;
        }
      }

      // Construct the full message including the quote
      const newMessage = {
        ...transformMessage({
          ...payload.new,
          read_at: new Date().toISOString()
        }, userDbId, payload.new.sender_id === userDbId ? profile?.avatar_url : undefined),
        reply_to_message: originalMessage ? {
          id: originalMessage.id,
          content: originalMessage.content,
          sender_id: originalMessage.sender_id
        } : undefined
      };

   setMessages((prev) => [...prev, newMessage]);

/* 1️⃣ server‑side clear */
await supabase.rpc('mark_messages_as_read', {
  conversation_id_param: conversationId,
});

/* 2️⃣ local suppression so badge never blinks */
const toClear = notifications
  .filter(
    (n) =>
      !n.read_status &&
      n.type === 'new_message' &&
      n.metadata?.conversationId === conversationId
  )
  .map((n) => n.id);

if (toClear.length) {
  // UI store
  markAsReadBulk(toClear);

  // persist to DB (fire‑and‑forget)
  supabase
    .from('notifications')
    .update({ read_status: true })
    .in('id', toClear)
    .select();
}

fetchConversations();

    }
  })
  .subscribe();

    return () => {
      if (messageChannelRef.current) supabase.removeChannel(messageChannelRef.current);
    };
  }, [conversationId, userDbId, notifications, profile?.avatar_url, markAsReadBulk,fetchConversations]);

  // Subscribe to conversation updates
  useEffect(() => {
    if (!user?.id) return;
    if (conversationChannelRef.current) supabase.removeChannel(conversationChannelRef.current);

    conversationChannelRef.current = supabase
      .channel('conversations')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'conversations',
      }, () => {
        fetchConversations();
      })
      .subscribe();

    return () => {
      if (conversationChannelRef.current) supabase.removeChannel(conversationChannelRef.current);
    };
  }, [user?.id, fetchConversations]);

  // Clean up all subscriptions on unmount
  useEffect(() => {
    return () => {
      if (messageChannelRef.current) supabase.removeChannel(messageChannelRef.current);
      if (conversationChannelRef.current) supabase.removeChannel(conversationChannelRef.current);
      if (likesChannelRef.current) supabase.removeChannel(likesChannelRef.current);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  // Fetch conversations on mount.
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  return {
    messages,
    isLoading,
    error,
    messageInput,
    setMessageInput,
    isTyping,
    isUploadingImage,
    conversations,
    connectedConversations,
    nonConnectedConversations,
    isInitialized,
    messagesContainerRef,
    handleSendMessage: useCallback((convId: string, replyToMessageId?: string | null) => {
      if (messageInput?.trim()) {
        handleSendMessage(convId, messageInput, replyingTo || null);
      }
      setMessageInput('');
    }, [messageInput, handleSendMessage, replyingTo]),
    handleImageUpload,
    handleLikeMessage,
    handleEditMessage,
    handleDeleteMessage,
    editingMessageId,
    setEditingMessageId,
    handleReplyToMessage: useCallback((message: Message) => {
      setReplyingTo(message);
    }, []),
    replyingTo,
    setReplyingTo,
    fetchConversations,
    likedMessages
  };
}

export { useChat };